<script setup lang="ts">
import { AlertCircle, CheckCircle2, FileText, FileUp, X } from 'lucide-vue-next'
import { ref } from 'vue'

import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '@/lib/constants'

interface UploadDoc {
  id: number
  name: string
  status: string
  rejection_reason: string | null
}

interface Props {
  doc: UploadDoc
  selectedFile: File | null
  error: string | null
  shadow?: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  select: [file: File]
  error: [message: string]
  clear: []
}>()

const fileInput = ref<HTMLInputElement | null>(null)

function triggerInput() {
  fileInput.value?.click()
}

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  if (!file) return
  if (!ALLOWED_MIME_TYPES.includes(file.type as (typeof ALLOWED_MIME_TYPES)[number])) {
    emit('error', 'Invalid file type. Only PDF, JPEG, and PNG are accepted.')
    input.value = ''
    return
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    emit('error', `File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`)
    input.value = ''
    return
  }
  emit('select', file)
}

function clearFile() {
  if (fileInput.value) fileInput.value.value = ''
  emit('clear')
}
</script>

<template>
  <div
    class="overflow-hidden rounded-xl border bg-card transition-all duration-200"
    :class="{
      'shadow-sm': shadow,
      'border-emerald-400 dark:border-emerald-700': selectedFile,
      'border-red-400 dark:border-red-700': error,
      'border-border': !selectedFile && !error,
    }"
  >
    <!-- Rejection reason -->
    <div v-if="doc.rejection_reason" class="flex items-start gap-2 border-b border-red-200 bg-red-50 px-3 py-1.5 dark:border-red-900 dark:bg-red-950/30">
      <AlertCircle class="mt-0.5 size-3 shrink-0 text-red-500 dark:text-red-400" />
      <p class="text-xs text-red-700 dark:text-red-400">{{ doc.rejection_reason }}</p>
    </div>

    <!-- Full-width clickable row: name + upload control -->
    <div
      role="button"
      tabindex="0"
      class="flex w-full cursor-pointer flex-col items-stretch gap-2.5 px-3 py-2.5 transition-colors sm:flex-row sm:items-center"
      :class="{
        'bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50': selectedFile,
        'bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/40': error,
        'bg-muted/50 hover:bg-muted dark:hover:bg-muted/70': !selectedFile && !error,
      }"
      @click="triggerInput"
      @keydown.enter="triggerInput"
      @keydown.space.prevent="triggerInput"
    >
      <!-- Icon + name: always in line -->
      <div class="flex min-w-0 items-center gap-2.5 sm:flex-1">
        <div
          class="flex size-6 shrink-0 items-center justify-center rounded-full"
          :class="{
            'bg-emerald-100 dark:bg-emerald-900/60': selectedFile,
            'bg-red-100 dark:bg-red-900/60': error,
            'bg-amber-100 dark:bg-amber-900/40': !selectedFile && !error,
          }"
        >
          <CheckCircle2 v-if="selectedFile" class="size-3.5 text-emerald-600 dark:text-emerald-400" />
          <AlertCircle v-else-if="error" class="size-3.5 text-red-500 dark:text-red-400" />
          <FileText v-else class="size-3.5 text-amber-600 dark:text-amber-400" />
        </div>

        <div class="min-w-0">
          <p class="truncate text-sm font-semibold leading-tight">{{ doc.name }}</p>
          <p
            class="text-xs leading-tight"
            :class="{
              'text-emerald-600 dark:text-emerald-400': selectedFile,
              'text-red-500 dark:text-red-400': error,
              'text-amber-600 dark:text-amber-400': !selectedFile && !error,
            }"
          >
            <span v-if="selectedFile">Ready to upload</span>
            <span v-else-if="error">Invalid file — try again</span>
            <span v-else-if="doc.status === 'rejected'">Re-upload required</span>
            <span v-else>Needs a file</span>
          </p>
        </div>
      </div>

      <!-- Upload prompt: under the name on mobile, beside it from sm breakpoint up -->
      <div
        v-if="!selectedFile"
        class="flex items-center gap-2 rounded-lg border-2 border-dashed px-2.5 py-1.5 sm:w-auto sm:shrink-0"
        :class="error ? 'border-red-300 dark:border-red-800' : 'border-border'"
      >
        <FileUp class="size-4 shrink-0" :class="error ? 'text-red-400' : 'text-muted-foreground'" />
        <span class="whitespace-nowrap text-sm font-medium" :class="error ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'">
          Click to choose file
        </span>
        <span class="whitespace-nowrap text-xs text-muted-foreground/60">PDF · JPEG · PNG</span>
      </div>

      <!-- File selected -->
      <div
        v-else
        class="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-100/60 px-2.5 py-1.5 dark:border-emerald-800 dark:bg-emerald-900/30 sm:w-auto sm:shrink-0"
      >
        <FileText class="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span class="min-w-0 flex-1 truncate text-sm font-medium text-emerald-800 dark:text-emerald-300 sm:max-w-40 sm:flex-none">
          {{ selectedFile?.name }}
        </span>
        <button
          type="button"
          class="shrink-0 rounded p-0.5 text-emerald-500 hover:bg-emerald-200 hover:text-emerald-700 dark:hover:bg-emerald-900/50"
          title="Remove"
          @click.stop="clearFile"
        >
          <X class="size-3.5" />
        </button>
      </div>

      <!-- Hidden input -->
      <input
        ref="fileInput"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        class="sr-only"
        @change="onFileChange"
      />
    </div>

    <!-- Validation error -->
    <p v-if="error" class="flex items-center gap-1 px-3 pb-2.5 text-xs text-red-600 dark:text-red-400">
      <AlertCircle class="size-3 shrink-0" />
      {{ error }}
    </p>
  </div>
</template>
